import { ChevronDown, ChevronUp, Heart } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { Combobox } from "#app/components/forms.tsx";
import { Button } from "#app/components/ui/button.tsx";
import { Input } from "#app/components/ui/input.tsx";
import { Label } from "#app/components/ui/label.tsx";
import { RadioGroup, RadioGroupItem } from "#app/components/ui/radio-group.tsx";
import { Separator } from "#app/components/ui/separator.tsx";
import { formatState, formatText } from "#app/utils/format-text.tsx";

type ComboboxItem = {
  value: string;
  label: string;
};

interface CarFavorite {
  carId: number;
}

interface FilterListProps {
  handleParamsChange: (
    e:
      | React.MouseEvent<HTMLButtonElement>
      | React.ChangeEvent<HTMLInputElement>,
  ) => void;
  handleValueChange: (value: string, inputName: string) => void;
  params: Record<string, string>;
  listMakers: ComboboxItem[];
  listModels: ComboboxItem[];
  listCities: ComboboxItem[];
  listStates?: ComboboxItem[];
  favorites?: CarFavorite[] | Array<{ carId: number }>;
}

interface FilterSectionProps {
  title: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

type InputErrors = {
  ano?: string | null;
  preco?: string | null;
  quilometragem?: string | null;
};

// Helper function to format numeric strings
const formatNumericString = (value: string | undefined): string => {
  if (value === null || value === undefined) return "";
  const numericValue = String(value).replace(/\D/g, "");
  if (!numericValue) return "";
  // Avoid issues with large numbers if not parsed correctly
  try {
    return parseInt(numericValue, 10).toLocaleString("pt-BR");
  } catch (e) {
    return ""; // Return empty if parsing fails
  }
};

// Helper function to clean numeric strings
const cleanNumericString = (value: string | undefined): string => {
  if (value === null || value === undefined) return "";
  return String(value).replace(/\D/g, "");
};

// Validation function for ranges
const validateInputRange = (
  minValStr: string,
  maxValStr: string,
): string | null => {
  const minVal = parseInt(minValStr, 10);
  const maxVal = parseInt(maxValStr, 10);

  if (!isNaN(minVal) && !isNaN(maxVal) && maxVal < minVal) {
    return 'Valor "Até" deve ser maior ou igual a "De"';
  }
  return null;
};

const FilterSection = ({
  title,
  children,
  defaultOpen = true,
}: FilterSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="py-3">
      <button
        className="flex w-full items-center justify-between py-2"
        onClick={() => setIsOpen(!isOpen)}
        type="button"
      >
        <h3 className="text-lg font-medium text-gray-900">{title}</h3>
        {isOpen ? (
          <ChevronUp className="h-4 w-4 text-gray-500" />
        ) : (
          <ChevronDown className="h-4 w-4 text-gray-500" />
        )}
      </button>

      {isOpen && <div className="mt-3 space-y-4">{children}</div>}

      <Separator className="mt-3" />
    </div>
  );
};

const DEBOUNCE_DELAY = 500;

export default function FilterList({
  handleParamsChange,
  handleValueChange,
  params,
  listMakers,
  listModels,
  listCities,
  listStates,
  favorites,
}: FilterListProps) {
  const timeoutRefs = useRef<Record<string, NodeJS.Timeout>>({});
  const [inputErrors, setInputErrors] = useState<InputErrors>({});
  const debouncedInputValuesRef = useRef<Record<string, string>>({}); // Ref to hold latest values for debounce callback

  const [debouncedInputValues, _setDebouncedInputValues] = useState<
    Record<string, string>
  >(() => {
    const initialValues = {
      "ano-minimo": params["ano-minimo"] || "",
      "ano-maximo": params["ano-maximo"] || "",
      "preco-minimo": formatNumericString(params["preco-minimo"]),
      "preco-maximo": formatNumericString(params["preco-maximo"]),
      "quilometragem-minima": formatNumericString(
        params["quilometragem-minima"],
      ),
      "quilometragem-maxima": formatNumericString(
        params["quilometragem-maxima"],
      ),
    };
    debouncedInputValuesRef.current = initialValues; // Initialize ref
    return initialValues;
  });

  // Function to update state and ref together
  const setDebouncedInputValues = (
    updater:
      | Record<string, string>
      | ((prev: Record<string, string>) => Record<string, string>),
  ) => {
    const newValues =
      typeof updater === "function"
        ? updater(debouncedInputValuesRef.current)
        : updater;
    debouncedInputValuesRef.current = newValues;
    _setDebouncedInputValues(newValues);
  };

  // Effect to sync params and validate initial ranges
  useEffect(() => {
    const initialValues = {
      "ano-minimo": params["ano-minimo"] || "",
      "ano-maximo": params["ano-maximo"] || "",
      "preco-minimo": formatNumericString(params["preco-minimo"]),
      "preco-maximo": formatNumericString(params["preco-maximo"]),
      "quilometragem-minima": formatNumericString(
        params["quilometragem-minima"],
      ),
      "quilometragem-maxima": formatNumericString(
        params["quilometragem-maxima"],
      ),
    };
    setDebouncedInputValues(initialValues);

    // Initial validation
    const errors: InputErrors = {};
    errors.ano = validateInputRange(
      cleanNumericString(initialValues["ano-minimo"]),
      cleanNumericString(initialValues["ano-maximo"]),
    );
    errors.preco = validateInputRange(
      cleanNumericString(initialValues["preco-minimo"]),
      cleanNumericString(initialValues["preco-maximo"]),
    );
    errors.quilometragem = validateInputRange(
      cleanNumericString(initialValues["quilometragem-minima"]),
      cleanNumericString(initialValues["quilometragem-maxima"]),
    );
    setInputErrors(errors);
  }, [params]);

  const handleDebouncedInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const { id, value } = e.target;
    let numericValue = value;
    let formattedValue = value;
    let category: keyof InputErrors | null = null;
    let isMinField = false;

    if (id.startsWith("ano-")) category = "ano";
    else if (id.startsWith("preco-")) category = "preco";
    else if (id.startsWith("quilometragem-")) category = "quilometragem";

    if (category) {
      isMinField = id.endsWith("-minimo") || id.endsWith("-minima");
      numericValue = cleanNumericString(value);
      // Only format price and mileage for display
      formattedValue =
        category === "preco" || category === "quilometragem"
          ? formatNumericString(numericValue)
          : numericValue;
    }

    // Update the displayed value immediately
    const newValues = {
      ...debouncedInputValuesRef.current,
      [id]: formattedValue,
    };
    setDebouncedInputValues(newValues);

    // --- Immediate Validation ---
    if (category) {
      const minId =
        category === "quilometragem"
          ? `${category}-minima`
          : `${category}-minimo`;
      const maxId =
        category === "quilometragem"
          ? `${category}-maxima`
          : `${category}-maximo`;
      const currentMinStr = cleanNumericString(newValues[minId]);
      const currentMaxStr = cleanNumericString(newValues[maxId]);

      const error = validateInputRange(currentMinStr, currentMaxStr);
      setInputErrors((prevErrors) => ({
        ...prevErrors,
        [category!]: error,
      }));
    }
    // --- End Immediate Validation ---

    if (timeoutRefs.current[id]) {
      clearTimeout(timeoutRefs.current[id]);
    }

    timeoutRefs.current[id] = setTimeout(() => {
      // --- Debounced Validation & Action ---
      let errorForDebounce: string | null = null;
      if (category) {
        const latestValues = debouncedInputValuesRef.current;
        const minId =
          category === "quilometragem"
            ? `${category}-minima`
            : `${category}-minimo`;
        const maxId =
          category === "quilometragem"
            ? `${category}-maxima`
            : `${category}-maximo`;
        const latestMinStr = cleanNumericString(latestValues[minId]);
        const latestMaxStr = cleanNumericString(latestValues[maxId]);
        errorForDebounce = validateInputRange(latestMinStr, latestMaxStr);
      }

      // Only proceed if the range is valid
      if (errorForDebounce === null) {
        const syntheticEvent = {
          target: {
            id: id,
            value: numericValue, // Send the cleaned value
          },
          currentTarget: e.currentTarget,
        } as unknown as React.ChangeEvent<HTMLInputElement>;
        handleParamsChange(syntheticEvent);
      } else {
        // Optional: Log or handle the case where debounce is skipped due to validation error
        console.log(
          `Debounced update for ${id} skipped due to validation error.`,
        );
      }
      // --- End Debounced Validation & Action ---
    }, DEBOUNCE_DELAY);
  };

  useEffect(() => {
    const timeouts = timeoutRefs.current;
    return () => {
      Object.values(timeouts).forEach(clearTimeout);
    };
  }, []);

  return (
    <div className="h-full overflow-y-auto bg-white px-4 pb-16 sm:px-6">
      <div className="sticky top-0 bg-white pt-7 pb-2 z-10">
        <h2 className="text-xl font-bold text-gray-900">Filtros</h2>
      </div>

      {favorites && favorites.length > 0 && (
        <div className="my-4">
          <Button
            className={`w-full flex items-center justify-center gap-2 transition-colors ${
              params.favoritos === "sim"
                ? "bg-primary text-white hover:bg-primary/90"
                : "bg-white text-gray-800 hover:bg-gray-50 border border-gray-200"
            }`}
            id="favoritos"
            aria-label="Meus Favoritos"
            value={params.favoritos === "sim" ? "" : "sim"}
            onClick={(e) => handleParamsChange(e)}
            variant={params.favoritos === "sim" ? "default" : "outline"}
          >
            <Heart
              className={`h-4 w-4 ${params.favoritos === "sim" ? "fill-white" : ""}`}
            />
            Meus Favoritos
          </Button>
        </div>
      )}

      <div className="space-y-1">
        <FilterSection title="Condição de Uso">
          <RadioGroup
            defaultValue={params.condicao || ""}
            className="space-y-2"
          >
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value=""
                id="condicao-todos"
                onClick={(e) => handleParamsChange(e)}
                aria-label="Todos"
              />
              <Label
                htmlFor="condicao-todos"
                className="cursor-pointer"
                onClick={() => {
                  const event = {
                    target: { id: "condicao-todos", value: "" },
                  } as unknown as React.MouseEvent<HTMLButtonElement>;
                  handleParamsChange(event);
                }}
              >
                Todos
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="new"
                id="condicao-novo"
                onClick={(e) => handleParamsChange(e)}
                aria-label="Novo"
              />
              <Label
                htmlFor="condicao-novo"
                className="cursor-pointer"
                onClick={() => {
                  const event = {
                    target: { id: "condicao-novo", value: "new" },
                  } as unknown as React.MouseEvent<HTMLButtonElement>;
                  handleParamsChange(event);
                }}
              >
                Novo
              </Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem
                value="used"
                id="condicao-usado"
                onClick={(e) => handleParamsChange(e)}
                aria-label="Usado"
              />
              <Label
                htmlFor="condicao-usado"
                className="cursor-pointer"
                onClick={() => {
                  const event = {
                    target: { id: "condicao-usado", value: "used" },
                  } as unknown as React.MouseEvent<HTMLButtonElement>;
                  handleParamsChange(event);
                }}
              >
                Seminovo
              </Label>
            </div>
          </RadioGroup>
        </FilterSection>

        <FilterSection title="Veículo">
          <Combobox
            labelProps={{
              children: "Marca",
              className: "text-sm font-medium text-gray-700",
            }}
            inputProps={{
              "aria-label": "Marca",
              className: "w-full",
            }}
            inputName="marca"
            defaultData={params.marca || ""}
            handleStateChange={(value, inputName) => {
              handleValueChange(value, inputName);

              if (inputName === "marca" && params.modelo) {
                setTimeout(() => {
                  handleValueChange("", "modelo");
                }, 0);
              }
            }}
            showErrors={false}
            items={listMakers.map((maker) => ({
              ...maker,
              label: formatText(maker.value),
            }))}
          />

          <div className="mt-4">
            <Combobox
              labelProps={{
                children: "Modelo",
                className: "text-sm font-medium text-gray-700",
              }}
              inputProps={{
                "aria-label": "Modelo",
                value: params.modelo || "",
                className: "w-full",
              }}
              inputName="modelo"
              defaultData={params.modelo || ""}
              handleStateChange={(value, inputName) =>
                handleValueChange(value, inputName)
              }
              items={listModels.map((model) => ({
                ...model,
                label: formatText(model.value),
              }))}
            />
          </div>
        </FilterSection>

        <FilterSection title="Detalhes">
          <div className="space-y-5">
            {[
              { id: "ano", label: "Ano", type: "number" },
              {
                id: "preco",
                label: "Preço",
                type: "text",
                inputMode: "numeric",
                pattern: "[0-9]*",
              },
              {
                id: "quilometragem",
                label: "Quilometragem",
                type: "text",
                inputMode: "numeric",
                pattern: "[0-9]*",
              },
            ].map((item) => {
              const category = item.id as keyof InputErrors;
              const minId =
                item.id === "quilometragem"
                  ? `${item.id}-minima`
                  : `${item.id}-minimo`;
              const maxId =
                item.id === "quilometragem"
                  ? `${item.id}-maxima`
                  : `${item.id}-maximo`;
              const hasError = !!inputErrors[category];
              const errorClass = hasError
                ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                : "border-gray-300 focus:border-indigo-500 focus:ring-indigo-500";

              return (
                <div key={item.id} className="space-y-2">
                  <Label
                    htmlFor={minId}
                    className="flex items-center gap-1.5 text-sm font-medium text-gray-700"
                  >
                    <span>{item.label}</span>
                  </Label>
                  <div className="flex gap-2">
                    <div className="flex-1">
                      <Input
                        type={item.type as any}
                        inputMode={item.inputMode as any}
                        pattern={item.pattern}
                        placeholder="De"
                        id={minId}
                        value={debouncedInputValues[minId] || ""}
                        onChange={handleDebouncedInputChange}
                        className={`w-full rounded-md shadow-sm sm:text-sm ${errorClass}`}
                        min={item.type === "number" ? "0" : undefined}
                      />
                    </div>
                    <div className="flex-1">
                      <Input
                        type={item.type as any}
                        inputMode={item.inputMode as any}
                        pattern={item.pattern}
                        placeholder="Até"
                        id={maxId}
                        value={debouncedInputValues[maxId] || ""}
                        onChange={handleDebouncedInputChange}
                        className={`w-full rounded-md shadow-sm sm:text-sm ${errorClass}`}
                        min={item.type === "number" ? "0" : undefined}
                      />
                    </div>
                  </div>
                  {hasError && (
                    <p className="mt-1 text-xs text-red-600">
                      {inputErrors[category]}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </FilterSection>

        <FilterSection title="Localização">
          <Combobox
            labelProps={{
              children: "Estado",
              className: "text-sm font-medium text-gray-700",
            }}
            inputProps={{
              "aria-label": "Estado",
              className: "w-full",
            }}
            inputName="estado"
            defaultData={params.estado || ""}
            handleStateChange={(value, inputName) => {
              handleValueChange(value, inputName);

              if (inputName === "estado" && params.cidade) {
                setTimeout(() => {
                  handleValueChange("", "cidade");
                }, 0);
              }
            }}
            showErrors={false}
            items={(
              listStates || [
                { label: "Acre", value: "acre (ac)" },
                { label: "Alagoas", value: "alagoas (al)" },
                { label: "Amapá", value: "amapá (ap)" },
                { label: "Amazonas", value: "amazonas (am)" },
                { label: "Bahia", value: "bahia (ba)" },
                { label: "Ceará", value: "ceará (ce)" },
                { label: "Distrito Federal", value: "distrito federal (df)" },
                { label: "Espírito Santo", value: "espírito santo (es)" },
                { label: "Goiás", value: "goiás (go)" },
                { label: "Maranhão", value: "maranhão (ma)" },
                { label: "Mato Grosso", value: "mato grosso (mt)" },
                {
                  label: "Mato Grosso do Sul",
                  value: "mato grosso do sul (ms)",
                },
                { label: "Minas Gerais", value: "minas gerais (mg)" },
                { label: "Pará", value: "pará (pa)" },
                { label: "Paraíba", value: "paraíba (pb)" },
                { label: "Paraná", value: "paraná (pr)" },
                { label: "Pernambuco", value: "pernambuco (pe)" },
                { label: "Piauí", value: "piauí (pi)" },
                { label: "Rio de Janeiro", value: "rio de janeiro (rj)" },
                {
                  label: "Rio Grande do Norte",
                  value: "rio grande do norte (rn)",
                },
                { label: "Rio Grande do Sul", value: "rio grande do sul (rs)" },
                { label: "Rondônia", value: "rondônia (ro)" },
                { label: "Roraima", value: "roraima (rr)" },
                { label: "Santa Catarina", value: "santa catarina (sc)" },
                { label: "São Paulo", value: "são paulo (sp)" },
                { label: "Sergipe", value: "sergipe (se)" },
                { label: "Tocantins", value: "tocantins (to)" },
              ]
            ).map((state) => ({
              ...state,
              label: formatState(state.value),
            }))}
          />

          <div className="mt-4">
            <Combobox
              labelProps={{
                children: "Cidade",
                className: "text-sm font-medium text-gray-700",
              }}
              inputProps={{
                "aria-label": "Cidade",
                className: "w-full",
              }}
              inputName="cidade"
              defaultData={params.cidade || ""}
              handleStateChange={(value, inputName) =>
                handleValueChange(value, inputName)
              }
              showErrors={false}
              items={listCities}
            />
          </div>
        </FilterSection>
      </div>
    </div>
  );
}
